import { provideNzIcons } from 'ng-zorro-antd/icon';
import { IcIcons } from 'libs/core/src/lib/configs/icons';
import { ClientsService } from 'libs/core/src/lib/services/clients.service';
import { ConfigInjectionToken } from 'libs/core/src/lib/configs/constants';
import { environment } from 'libs/core/src/lib/environments/environment';
import { hasFeature } from 'libs/core/src/lib/utils/feature-flags';
import { IcHttpConfigInterceptor } from 'libs/core/src/lib/interceptors/ic-http-interceptor.interceptor';
import { provideDarkMode } from 'libs/core/src/lib/services/dark-mode.service';
import { providePortalName } from 'libs/core/src/lib/services/notice.service';
import { provideImpersonationMode } from 'libs/core/src/lib/services/impersonation.service';
import { IcHttpCachingConfig } from 'libs/core/src/lib/configs/http-cache';
import { InsurerService } from 'libs/core/src/lib/services/insurer.service';
import { PartnersService } from 'libs/core/src/lib/services/partners.service';
import { provideTenantTheme } from 'libs/core/src/lib/services/tenant-config.service';
import { RouteTitleStrategy } from 'libs/core/src/lib/route-title-stratagy';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  provideRouter,
  Route,
  TitleStrategy,
  withComponentInputBinding,
} from '@angular/router';
import {
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { ModuleConfig } from '@inshuwa-client/models';
import { NZ_CONFIG, NzConfig } from 'ng-zorro-antd/core/config';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { NgHttpCachingModule } from 'ng-http-caching';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { AppComponent } from './app/app.component';
import {
  initializeSentry,
  provideSentryTracing,
} from '../../../libs/core/src/lib/sentry';
import { PaymentSuccessPageComponent } from './app/components/payment-success-page.component';
import { clientResolver } from './app/auth/client.resolver';
import { clientLoginGuard } from './app/auth/client-login.guard';
import { provideNzDateFnsAdapter } from 'ng-zorro-antd/core/time';


const ngZorroConfig: NzConfig = {
  theme: {
    primaryColor: '#1890ff',
  },
};

// Was 9 call-sites of inline `environment.name === 'X' || ...` OR-chains (3 distinct conditions,
// copy-pasted) - a single build could only ever serve one hardcoded environment.name, so these
// couldn't resolve correctly once builds stopped being per-tenant. hasFeature reads
// environment.featureFlags, which TenantConfigService populates at runtime from the resolved
// tenant's TenantConfig.FeatureFlags. Defaults to false/unset for any tenant that hasn't had
// these flags configured yet - the same fallback every currently-unlisted tenant already gets.
const usesStandardCharteredUnderwriting = () => hasFeature('standardCharteredUnderwriting');
const usesV2Storefront = () => hasFeature('v2Storefront');
const usesV2Dashboard = () => hasFeature('v2Dashboard');

const routes: Route[] = [
  {
    path: '',
    loadChildren: () =>
      import('./app/v1/buy-cover/underwriting.module').then(
        (m) => m.UnderwritingModule,
      ),
    title: 'Buy cover',
    canMatch: [usesStandardCharteredUnderwriting],
  },
  {
    path: '',
    loadComponent: () =>
      import('./app/v2/buy-cover/product-type-landing.component').then(
        (m) => m.ProductTypeLandingComponent,
      ),
    title: 'Buy cover',
    canMatch: [usesV2Storefront],
  },
  {
    path: 'motor',
    loadComponent: () =>
      import('./app/v2/buy-cover/motor/motor-quote-form.component').then(
        (m) => m.MotorQuoteFormComponent,
      ),
    title: 'Motor Cover',
    canMatch: [usesV2Storefront],
  },
  {
    path: 'life',
    loadComponent: () =>
      import('./app/v2/buy-cover/life/life-quote-form.component').then(
        (m) => m.LifeQuoteFormComponent,
      ),
    title: 'Life Cover',
    canMatch: [usesV2Storefront],
  },
  {
    path: 'travel',
    loadComponent: () =>
      import('./app/v2/buy-cover/travel/travel-quote-form.component').then(
        (m) => m.TravelQuoteFormComponent,
      ),
    title: 'Travel Cover',
    canMatch: [usesV2Storefront],
  },
  {
    path: 'general',
    loadComponent: () =>
      import('./app/v2/buy-cover/general/general-quote-form.component').then(
        (m) => m.GeneralQuoteFormComponent,
      ),
    title: 'General Insurance',
    canMatch: [usesV2Storefront],
  },
  {
    path: '',
    loadChildren: () =>
      import('./app/auth/client-auth.module').then((m) => m.ClientAuthModule),
    title: 'Login',
  },

  {
    path: 'buy-cover',
    loadChildren: () =>
      import('./app/v1/buy-cover/underwriting.module').then(
        (m) => m.UnderwritingModule,
      ),
    title: 'Buy cover',
  },

  {
    path: 'dashboard',
    resolve: { generalClient: clientResolver },
    canActivate: [clientLoginGuard],
    canMatch: [usesV2Dashboard],
    loadChildren: () =>
      import('./app/v2/dashboard/dashboard-routing-module-v2.module').then(
        (m) => m.DashboardRoutingModuleV2,
      ),
  },
  {
    path: 'dashboard',
    resolve: { generalClient: clientResolver },
    canActivate: [clientLoginGuard],
    loadChildren: () =>
      import('./app/v1//dashboard/dashboard.module').then(
        (m) => m.DashboardModule,
      ),
  },
  {
    path: 'payment-success',
    component: PaymentSuccessPageComponent,
  },
  {
    path: 'profile',
    loadComponent: () =>
      import('./app/components/profile.component').then(
        (m) => m.ClientProfileComponent,
      ),
    title: 'Profile Info',
    canMatch: [usesV2Storefront],
  },
];

initializeSentry('Clients Portal');

bootstrapApplication(AppComponent, {
  providers: [
    providePortalName('clients-portal'),
    provideNzIcons(IcIcons),
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      FormsModule,
      LoadingBarModule,
      LoadingBarHttpClientModule,
      NgHttpCachingModule.forRoot(IcHttpCachingConfig),
      LoadingBarRouterModule,
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IcHttpConfigInterceptor,
      multi: true,
    },
    { provide: NZ_I18N, useValue: en_US },
    { provide: NzNotificationService },
    { provide: PartnersService },
    { provide: NzModalService },
    { provide: TitleStrategy, useClass: RouteTitleStrategy },
    provideImpersonationMode(),
    provideTenantTheme(),
    provideDarkMode(),
    {
      provide: ConfigInjectionToken,
      useFactory: () => {
        const insurerService = inject(InsurerService);
        const partnerService = inject(PartnersService);
        const config: ModuleConfig = {
          branchId: null,
          // Getters, not values: the app boots before login, so a snapshot stays null until reload.
          get clientId() {
            return ClientsService.getLoggedInClient()?.id ?? null;
          },
          get client() {
            return ClientsService.getLoggedInClient();
          },
          intermediary: null,
          intermediaryId: environment.intermediaryId ?? null,
          portalName: environment.portalName,
          mode: environment.partnerId
            ? 'partner-client'
            : environment.intermediaryId
              ? 'broker-client'
              : environment.insurerId
                ? 'insurer-client'
                : 'direct-client',
          insurer: insurerService.getFromLocalStorage(),
          insurerId: environment.insurerId,
          partner: partnerService.getFromLocalStorage(),
          partnerId: environment.partnerId,
        };
        return config;
      },
    },
    { provide: NZ_CONFIG, useValue: ngZorroConfig },
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideSentryTracing(), provideNzDateFnsAdapter(),
  ],
}).catch((err) => console.error(err));
